import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useMemberCompanyTimezones } from '@/hooks/useMemberCompanyTimezones';
import { useMembersOptionsForSelect } from '@/hooks/useMembersOptionsForSelect';
import { timelineActionTypes } from '@/store/Timeline/Timeline.actionTypes';
import memberSessionsService from '@/services/memberSessions';
import { formatPercent } from '@/utils/numberUtils';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import httpClient from '@/config/httpClient';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlTimezoneIgnoreFlagSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@/hooks/useUrlFilterSync';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';
import {
  collectScreenshotsFromActivities,
  getScreenshotRevealKey,
} from '@/utils/screenshotPrivacy';
import { TimelineView } from './Timeline.view';

export const TimelineComponent = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const dispatch = useDispatch();
  const { sessionsByDay, loading, page, pageSize, filters, exportData } =
    useSelector((state) => {
      return state.timeline;
    });
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const history = useHistory();

  const teamId = team?.active?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;

  const [selectedMember, setSelectedMember] = useState(null);
  const memberId = selectedMember?.value || null;
  const employeeName = selectedMember?.label?.replace(/\s*\([^)]*\)\s*$/, '');
  const teamName = team?.active?.Team;
  const {
    membersOptions,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useMembersOptionsForSelect(teamId);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );

  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  useUrlDateRangeSync({ location, setDateRange: setRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember,
    setSelectedMember,
    currentUserId: auth?.profile?.id,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember,
  });
  useUrlTimezoneIgnoreFlagSync({ history, setIgnoreUrlTimezone });

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  const writeParams = useMemo(() => {
    const params = { start: range.start, end: range.end };
    if (selectedMember?.value) params.member = selectedMember.value;
    if (selectedTimezone) params.tz = selectedTimezone;
    return params;
  }, [range.start, range.end, selectedMember?.value, selectedTimezone]);

  useUrlFilterWrite({
    history,
    location,
    params: writeParams,
    enabled: Boolean(teamId && range?.start && range?.end),
  });

  const onMemberFilterChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMember(opt);
    },
    [setSelectedMember]
  );

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );
  const [localPage, setLocalPage] = useState(page || 1);
  const PAGE_SIZE = 10;
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState('');
  const [sessionActivities, setSessionActivities] = useState([]);
  const [activeSessionMeta, setActiveSessionMeta] = useState(null);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(0);
  const modalActivitiesCacheRef = useRef({});

  const getModalActivitiesCacheKey = (
    actualSessionId,
    timezone,
    segStart,
    segEnd
  ) =>
    `${actualSessionId}::${timezone || 'UTC'}::${segStart || ''}::${segEnd || ''}`;

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    return match?.employeeId ?? userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = (nextPage = localPage) => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: nextPage,
        pageSize: PAGE_SIZE,
        rate: getRateForUser(selectedMember.value),
      },
    });
  };
  useEffect(() => {
    if (selectedMember) {
      modalActivitiesCacheRef.current = {};
      fetchTimeline(1);
      setLocalPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  const buildCsvFromDays = (days, tz, meta = null) => {
    const escape = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const m = meta || {
      timezone: '-',
      memberName: '-',
      companyName: '-',
      teamName: '-',
      dateRange: '-',
    };

    const rows = [];
    (days || []).forEach((day) => {
      day.sessions.forEach((s) => {
        const { displayIdle: displayIdleSec, idlePercent } =
          normalizeIdleForDisplay(s.durationSec, s.idleSec);
        const productiveSec =
          s.productiveSec != null
            ? s.productiveSec
            : Math.max(0, (s.durationSec || 0) - displayIdleSec);
        rows.push({
          Timezone: m.timezone,
          Member: m.memberName,
          Company: m.companyName,
          Team: m.teamName,
          'Date range': m.dateRange,
          Date: day.dayLabel,
          Start: moment(s.start).tz(tz).format('hh:mm A'),
          End: s.end ? moment(s.end).tz(tz).format('hh:mm A') : '-',
          Duration: formatTimeFromSeconds(s.durationSec),
          'Idle time': formatTimeFromSeconds(displayIdleSec),
          'Productive time': formatTimeFromSeconds(productiveSec),
          'Idle %': formatPercent(idlePercent),
          'Activity %': formatPercent(s.activityPct),
          Project: s.project ?? '',
          Task: s.task ?? '',
        });
      });
    });
    const header = Object.keys(rows[0] || {});
    const lines = [
      header.join(','),
      ...rows.map((r) => header.map((h) => escape(r[h])).join(',')),
    ];
    return lines.join('\n');
  };

  const handleExport = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: localPage,
        pageSize: PAGE_SIZE,
        rate: getRateForUser(selectedMember.value),
        forExport: true,
      },
    });
  };

  useEffect(() => {
    if (exportData === undefined || exportData === null) return;
    const clearExportData = () => {
      dispatch({
        type: timelineActionTypes.SET_STATE,
        payload: { exportData: null },
      });
    };
    if (exportData.length === 0) {
      clearExportData();
      return;
    }
    const meta = {
      timezone: selectedTimezone || '-',
      memberName: selectedMember?.label ?? '-',
      companyName: auth?.profile?.activeCompany?.companyName ?? '-',
      teamName: team?.active?.Team ?? '-',
      dateRange: `${range.start} to ${range.end}`,
    };
    const csv = buildCsvFromDays(exportData, selectedTimezone, meta);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'timeline_export.csv';
    link.click();
    URL.revokeObjectURL(url);
    clearExportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const closeScreenshotsModal = () => {
    setScreenshotModalOpen(false);

    setActivitiesLoading(false);
    setActivitiesError('');
    setSessionActivities([]);
    setActiveSessionMeta(null);
    setSelectedScreenshotIndex(0);
  };
  // Helper function to filter activities by time range (for split segments)
  const filterActivitiesByTimeRange = (
    activities,
    segmentStart,
    segmentEnd,
    timezone
  ) => {
    if (!segmentStart || !segmentEnd) {
      return activities; // No filtering if no time range provided
    }

    const segmentStartMoment = moment(segmentStart).tz(timezone);
    const segmentEndMoment = moment(segmentEnd).tz(timezone);

    return activities.filter((activity) => {
      if (!activity.timestamp) return false;
      const activityMoment = moment(activity.timestamp).tz(timezone);
      // Include activities that are >= segmentStart and <= segmentEnd
      // This includes screenshots at the exact end time
      return (
        activityMoment.isSameOrAfter(segmentStartMoment) &&
        activityMoment.isSameOrBefore(segmentEndMoment)
      );
    });
  };

  const openScreenshotsModal = async (session, dayLabel) => {
    // Check if this is a split segment
    const isSplitSegment = session?.isSplit && session?.parentSessionId;
    const actualSessionId = isSplitSegment
      ? session.parentSessionId
      : session?.id;

    setActiveSessionMeta({
      sessionId: session?.id,
      dayLabel,
      start: session?.start,
      end: session?.end,
    });
    setActivitiesError('');
    setScreenshotModalOpen(true);

    if (!actualSessionId) {
      setActivitiesError('Session id not available.');
      setSessionActivities([]);
      return;
    }

    if (!teamId || !selectedMember?.value) {
      setActivitiesError('Missing team or user information.');
      setSessionActivities([]);
      return;
    }

    const cacheKey = getModalActivitiesCacheKey(
      actualSessionId,
      selectedTimezone,
      isSplitSegment ? session.start : null,
      isSplitSegment ? session.end : null
    );

    if (modalActivitiesCacheRef.current[cacheKey]) {
      setSessionActivities(modalActivitiesCacheRef.current[cacheKey]);
      setSelectedScreenshotIndex(0);
      setActivitiesLoading(false);
      return;
    }

    setSessionActivities([]);
    setActivitiesLoading(true);
    try {
      const employeeId = getEmployeeIdForUser(selectedMember.value);

      // Find the parent session's start time for path determination
      let sessionStartTime = session.start;
      if (isSplitSegment && sessionsByDay) {
        // Find the parent session or any segment with the same parentSessionId to get original start time
        for (const day of sessionsByDay) {
          const foundSession = day.sessions?.find(
            (s) =>
              s.id === actualSessionId ||
              (s.parentSessionId === actualSessionId && s.start)
          );
          if (foundSession?.start) {
            sessionStartTime = foundSession.start;
            break;
          }
        }
      }

      const result = await memberSessionsService.getSessionActivities({
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        sessionId: actualSessionId, // Use parent session ID for split segments
        sessionStartTime, // Pass session startTime to determine path
      });

      if (result.success) {
        let activities = result.data.activities || [];

        // If this is a split segment, filter activities by the segment's time range
        if (isSplitSegment && session.start && session.end) {
          activities = filterActivitiesByTimeRange(
            activities,
            session.start,
            session.end,
            selectedTimezone
          );
        }

        modalActivitiesCacheRef.current[cacheKey] = activities;
        setSessionActivities(activities);
        setSelectedScreenshotIndex(0);
      } else {
        setActivitiesError('Unable to load screenshots for this session.');
      }
    } catch (error) {
      console.error('Error loading session activities', error);
      setActivitiesError('Unable to load screenshots for this session.');
    } finally {
      setActivitiesLoading(false);
    }
  };

  const flattenedScreens = useMemo(() => {
    return collectScreenshotsFromActivities(sessionActivities).map((row) => ({
      ...row,
      revealKey: getScreenshotRevealKey(row),
    }));
  }, [sessionActivities]);

  useEffect(() => {
    if (!flattenedScreens.length) {
      setSelectedScreenshotIndex(0);
    } else if (selectedScreenshotIndex >= flattenedScreens.length) {
      setSelectedScreenshotIndex(0);
    }
  }, [flattenedScreens, selectedScreenshotIndex]);

  const onExport = async () => {
    try {
      await httpClient.post('/exportTimelineCsv', {
        loggedInUser: {
          id: auth?.profile?.id,
          email: auth?.profile?.email,
          name: auth?.profile?.name,
        },
        teamName: team?.active?.Team,
        companyName: auth?.profile?.activeCompany?.companyName,
        memberName: selectedMember?.label,
        teamId,
        userUID: selectedMember?.value,
        companyId,
        employeeId: getEmployeeIdForUser(selectedMember?.value),
        memberId: selectedMember?.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        rate: getRateForUser(selectedMember?.value),
      });
    } catch (error) {
      console.error('Error exporting timeline', error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <TimelineView
      membersOptions={membersOptions}
      selectedMember={selectedMember}
      onMemberFilterChange={onMemberFilterChange}
      membersLoading={membersLoading}
      membersError={membersError}
      membersErrorObj={membersErrorObj}
      refetchMembers={refetchMembers}
      timezoneOptions={timezoneOptions}
      selectedTimezone={selectedTimezone}
      onTimezoneChange={handleTimezoneFilterChange}
      timezonesLoading={timezonesLoading}
      date={range.start}
      onDateChange={(dateStr) => setRange({ start: dateStr, end: dateStr })}
      onRefresh={() => {
        fetchTimeline(1);
        setLocalPage(1);
      }}
      onExport={() => {
        setExportLoading(true);
        onExport();
      }}
      exportLoading={exportLoading}
      exportDisabled={!sessionsByDay?.length}
      loading={loading}
      sessionsByDay={sessionsByDay}
      onOpenScreenshotsModal={openScreenshotsModal}
      screenshotModalOpen={screenshotModalOpen}
      closeScreenshotsModal={closeScreenshotsModal}
      activitiesLoading={activitiesLoading}
      activitiesError={activitiesError}
      flattenedScreens={flattenedScreens}
      selectedScreenshotIndex={selectedScreenshotIndex}
      setSelectedScreenshotIndex={setSelectedScreenshotIndex}
      activeSessionMeta={activeSessionMeta}
      employeeName={employeeName}
      teamName={teamName}
    />
  );
};
